import { Suspense, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Route } from "react-router-dom";
import { Loader, ViewportContext } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { lazyWithRetry } from "@monorepo/helpers";
import theme from "@monorepo/styles/theme";
import Check from "@mui/icons-material/Check";
import { Paper, styled, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import { StepIconProps } from "@mui/material/StepIcon";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { RegisterContext } from "context/RegisterContext";

import RegistrationBanner from "components/RegistrationBanner/RegistrationBanner";

const CreatePassword = lazyWithRetry(() => import("components/CreatePassword/CreateRegisterPassword"));
const SmsVerification = lazyWithRetry(() => import("components/VerifySMSCode/VerifyRegistrationSMSCode"));
const PhoneVerification = lazyWithRetry(() => import("components/PhoneVerification/PhoneVerification"));
const RegisterConfirm = lazyWithRetry(() => import("components/RegisterConfirm/RegisterConfirm"));

const StepIconRoot = styled("div")<{ ownerState: { active?: boolean } }>(({ ownerState }) => ({
  color: theme.palette.mode === "dark" ? theme.palette.grey[700] : "#eaeaf0",
  display: "flex",
  height: 22,
  alignItems: "center",
  ...(ownerState.active && {
    color: theme.palette.primary.main,
  }),
  "& .StepIconRoot-completedIcon": {
    color: theme.palette.primary.main,
    zIndex: 1,
    fontSize: theme.typography.subtitle1.fontSize,
  },
  "& .StepIconRoot-circle": {
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
}));

function StepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  return (
    <StepIconRoot ownerState={{ active }} className={className}>
      {completed ? <Check className="StepIconRoot-completedIcon" /> : <div className="StepIconRoot-circle" />}
    </StepIconRoot>
  );
}

export const Register = () => {
  const { t } = useTranslation();
  const { activeStep } = useContext(RegisterContext);
  const { isMobile } = useContext(ViewportContext);

  const steps = [
    { key: 0, href: routeConfig.register.phoneVerification },
    { key: 1, href: routeConfig.register.smsVerification },
    { key: 2, href: routeConfig.register.createPassword },
    { key: 3, href: routeConfig.register.registerConfirm },
  ];

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={1}
          sx={{
            display: "grid",
            justifyContent: "center",
            marginLeft: "auto",
            marginRight: "auto",
            justifyItems: "center",
            alignContent: {
              xs: "flex-start",
              md: "center",
            },
            width: "100%",
            maxWidth: {
              xs: "350px",
              md: "864px",
            },
            minHeight: {
              md: "530px",
            },
            gridTemplateColumns: {
              xs: "100%",
              md: "50% 50%",
            },
          }}
        >
          {!isMobile && <RegistrationBanner />}
          <Box
            sx={{
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(1),
              rowGap: theme.spacing(1),
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignContent: {
                xs: "stretch",
              },
              width: {
                xs: "310px",
                md: "360px",
              },
              textAlign: "center",
              height: "100%",
              minHeight: "605px",
            }}
          >
            <Stepper sx={{ mb: theme.spacing(1), width: "110px", mx: "auto" }} activeStep={activeStep} alternativeLabel>
              {steps.map((step) => (
                <Step key={step.key}>
                  <StepLabel StepIconComponent={StepIcon} />
                </Step>
              ))}
            </Stepper>
            <Typography
              sx={{ mb: theme.spacing(1), color: theme.palette.text.secondary }}
              variant={"h6"}
              component={"h2"}
            >
              {t("register.registration")}
            </Typography>
            <Box></Box>
            <Suspense fallback={<Loader />}>
              <Route exact path={routeConfig.register.registerConfirm}>
                <RegisterConfirm />
              </Route>
              <Route exact path={[routeConfig.register.self, routeConfig.register.phoneVerification]}>
                <PhoneVerification />
              </Route>
              <Route exact path={routeConfig.register.smsVerification}>
                <SmsVerification />
              </Route>
              <Route exact path={routeConfig.register.createPassword}>
                <CreatePassword />
              </Route>
            </Suspense>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default Register;
